import {
  useSurveyWidgetQuery,
  invalidateSurveyResponses,
} from '@hotjar/feedback/domain/Surveys/query';
import { useCurrentSite } from '@hotjar/site';
import { useState, useEffect } from 'react';
import { useSurveyQuestions } from '@hotjar/feedback/hooks/survey/useSurveyQuestions';
import { useNotify } from '@hotjar/hotsauce';
import type { SurveyQuestion } from '@hotjar/feedback/domain/Surveys/Questions';
import type { Survey } from '@hotjar/feedback/domain/Surveys/Survey/Survey';
import type { Site } from '@hotjar/site';
import type { QuestionResults } from '@hotjar/feedback/domain/Surveys/Results/AggregatedResult';
import type { SurveyIdStatus } from '../hooks/useCurrentSurveyId';
import { useCurrentSurveyId } from '../hooks/useCurrentSurveyId';
import { useQuestionResults } from './useQuestionResults';

interface ResponsesViewData {
  isLoaded: boolean;
  site: Site | undefined;
  survey: Survey | undefined;
  questions: SurveyQuestion[] | undefined;
  questionResults: QuestionResults | undefined;
  surveyId: number | undefined;
  surveyIdStatus: SurveyIdStatus;
}

export const useLoadResultsViewData = (): ResponsesViewData => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const { data: site } = useCurrentSite();

  const { surveyId, status: surveyIdStatus, isSurveyIdLoaded } = useCurrentSurveyId(site?.id);

  // if the surveyIdParam changes, we are loading a new survey
  useEffect(() => {
    setIsLoaded(false);
  }, [surveyId]);
  const { questions, questionsStatus } = useSurveyQuestions(surveyId);
  const { data: survey, status: surveyStatus } = useSurveyWidgetQuery(site?.id, surveyId);
  const questionResults = useQuestionResults(surveyId);

  useEffect(() => {
    if (survey?.id === undefined || site?.id === undefined) return;
    invalidateSurveyResponses(site.id, survey.id);
  }, [survey?.id, site?.id]);

  useEffect(() => {
    if (surveyIdStatus === 'NotFound') {
      setIsLoaded(true);
      return;
    }

    if (
      surveyStatus === 'success' &&
      questionsStatus !== 'loading' &&
      questionResults !== undefined
    ) {
      setIsLoaded(true);
      return;
    }

    if (surveyStatus === 'error') {
      setIsLoaded(true);
    }
  }, [surveyStatus, questionsStatus, surveyId, surveyIdStatus, questionResults, isSurveyIdLoaded]);

  const { showToast } = useNotify();
  useEffect(() => {
    if (isLoaded && survey && questions === undefined) {
      showToast({ text: 'Error loading survey questions', kind: 'danger' });
    }
  }, [isLoaded, survey, questions, showToast]);

  return {
    isLoaded,
    site,
    survey,
    questions,
    surveyId,
    surveyIdStatus,
    questionResults,
  };
};
